import { useState, useEffect, useCallback } from "react";
import { X, Bell, Send, AlertTriangle, CreditCard, Wrench, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const TEMPLATES = [
  { key: "awaria", label: "Awaria", icon: AlertTriangle, color: "text-red-400",
    title: "Awaria sieci",
    body: "Informujemy o awarii w Państwa rejonie. Nasz zespół pracuje nad jak najszybszym przywróceniem usługi. Przepraszamy za utrudnienia." },
  { key: "platnosc", label: "Płatność", icon: CreditCard, color: "text-amber-400",
    title: "Przypomnienie o płatności",
    body: "Przypominamy o zbliżającym się terminie płatności za usługę internetową. Prosimy o uregulowanie należności, aby uniknąć przerwy w dostępie." },
  { key: "przeglad", label: "Przegląd", icon: Wrench, color: "text-cyan-400",
    title: "Zaplanowany przegląd",
    body: "Informujemy o zaplanowanych pracach serwisowych / przeglądzie sieci w Państwa rejonie. Możliwe krótkie przerwy w dostępie do usługi." },
];

export default function ClientPushDialog({ open, onClose, clientId, clientName, defaultTitle = "", defaultBody = "" }) {
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const broadcast = !clientId;

  const loadLog = useCallback(() => {
    const q = clientId ? `?client_id=${clientId}` : "";
    api.get(`/push/log${q}`).then((r) => setLog(r.data || [])).catch(() => setLog([]));
  }, [clientId]);

  useEffect(() => { if (open) { loadLog(); setTitle(defaultTitle || ""); setBody(defaultBody || ""); } }, [open, loadLog, defaultTitle, defaultBody]);

  if (!open) return null;

  const pickTemplate = (tpl) => { setTitle(t(tpl.title)); setBody(t(tpl.body)); };

  const send = async () => {
    if (!body.trim()) { toast.error(t("Wpisz treść powiadomienia")); return; }
    setBusy(true);
    try {
      const payload = { title: title.trim() || "FiberPass", body: body.trim() };
      if (clientId) payload.client_id = clientId;
      const { data } = await api.post("/push/send-client", payload);
      if (data.sent > 0) toast.success(broadcast ? `${t("Wysłano do klientów")}: ${data.recipients}` : t("Wysłano powiadomienie push"));
      else toast.warning(broadcast ? t("Żaden klient nie ma zainstalowanej aplikacji (brak subskrypcji push)") : t("Klient nie ma zainstalowanej aplikacji / brak subskrypcji push"));
      setBody(""); setTitle("");
      loadLog();
    } catch (e) {
      toast.error(t("Nie udało się wysłać powiadomienia"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1400] bg-black/60 flex items-center justify-center p-4" data-testid="client-push-dialog" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 sticky top-0 bg-slate-900">
          <h3 className="font-bold text-sm flex items-center gap-2"><Bell size={16} className="text-indigo-400" />
            {broadcast ? t("Powiadomienie do wszystkich klientów") : `${t("Powiadomienie push")}: ${clientName || ""}`}</h3>
          <button onClick={onClose} data-testid="push-dialog-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] text-slate-500 uppercase mb-1.5">{t("Szablony")}</div>
            <div className="grid grid-cols-3 gap-2">
              {TEMPLATES.map((tpl) => (
                <button key={tpl.key} onClick={() => pickTemplate(tpl)} data-testid={`push-template-${tpl.key}`}
                  className="flex flex-col items-center gap-1 rounded-md border border-slate-700 bg-slate-800/50 hover:bg-slate-700/60 py-2 text-xs">
                  <tpl.icon size={16} className={tpl.color} /> {t(tpl.label)}
                </button>
              ))}
            </div>
          </div>
          <label className="block text-[10px] text-slate-500 uppercase">{t("Tytuł")}
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="FiberPass"
              data-testid="push-title-input" className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-2.5 py-2 text-sm text-slate-200 outline-none focus:ring-1 focus:ring-indigo-500" /></label>
          <label className="block text-[10px] text-slate-500 uppercase">{t("Treść")}
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={t("Treść powiadomienia…")}
              data-testid="push-body-input" className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-2.5 py-2 text-sm text-slate-200 outline-none focus:ring-1 focus:ring-indigo-500 resize-none" /></label>
          <button onClick={send} disabled={busy} data-testid="push-send-btn"
            className="w-full bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 rounded py-2.5 text-sm font-medium flex items-center justify-center gap-2">
            <Send size={15} /> {busy ? t("Wysyłanie…") : (broadcast ? t("Wyślij do wszystkich klientów") : t("Wyślij powiadomienie"))}
          </button>

          <div className="pt-2 border-t border-slate-800">
            <div className="text-[10px] text-slate-500 uppercase mb-2 flex items-center gap-1.5"><History size={12} /> {t("Historia wysłanych")} ({log.length})</div>
            {log.length === 0 ? (
              <div className="text-xs text-slate-500">{t("Brak wysłanych powiadomień")}</div>
            ) : (
              <div className="space-y-1.5" data-testid="push-log-list">
                {log.map((r) => (
                  <div key={r.id} data-testid={`push-log-item-${r.id}`} className="rounded-md border border-slate-800 bg-slate-800/40 px-2.5 py-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-200 font-medium truncate">{r.title || "FiberPass"}</span>
                      <span className="text-[10px] text-slate-500 shrink-0 ml-2">{String(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 line-clamp-2">{r.body}</div>
                    <div className="text-[10px] text-slate-600 flex items-center gap-2 mt-0.5">
                      {r.broadcast ? <span className="text-amber-500">{t("do wszystkich")}</span> : null}
                      <span>{t("dostarczone")}: {r.sent}/{r.recipients}</span>
                      {r.by ? <span>· {r.by}</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
